/**
 * index.ts - Skinnr Skins - Default skin library
 * 
 * CHANGE HISTORY:
 * - 251125 danielbeer: Added complete FMwvTool light/dark/auto skins with 122 variables
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 * - 251122_2330 danielbeer: Added compact FileMaker-style skin
 */
import { compactSkin } from './compact';
import { fmwvtoolSkins } from './fmwvtool';

// Re-export FMwvTool skins for direct access
export { fmwvtoolDefault, fmwvtoolDefaultLight, fmwvtoolDefaultDark } from './fmwvtool';

// Define skins inline to avoid JSON import issues
const gridLight = {
  "id": "grid-light",
  "name": "Grid Light Theme",
  "description": "Light theme for grid views (GriddrView, TableView)",
  "mode": "light",
  "agGridTheme": "alpine",
  "variables": {
    "ag-background-color": "#ffffff",
    "ag-foreground-color": "#000000",
    "ag-border-color": "#e5e7eb",
    "ag-header-background-color": "#f9fafb",
    "ag-header-foreground-color": "#374151",
    "ag-odd-row-background-color": "#ffffff",
    "ag-row-hover-color": "#f3f4f6",
    "ag-selected-row-background-color": "#dbeafe",
    "ag-cell-horizontal-border": "#e5e7eb",
    "ag-font-family": "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    "ag-font-size": "13px",
    "ag-row-height": "42px",
    "ag-header-height": "48px"
  }
};

const gridDark = {
  "id": "grid-dark",
  "name": "Grid Dark Theme",
  "description": "Dark theme for grid views (GriddrView, TableView)",
  "mode": "dark",
  "agGridTheme": "alpine-dark",
  "variables": {
    "ag-background-color": "#1e293b",
    "ag-foreground-color": "#f1f5f9",
    "ag-border-color": "#334155",
    "ag-header-background-color": "#0f172a",
    "ag-header-foreground-color": "#f1f5f9",
    "ag-odd-row-background-color": "#1e293b",
    "ag-row-hover-color": "#334155",
    "ag-selected-row-background-color": "#1e40af",
    "ag-cell-horizontal-border": "#334155",
    "ag-font-family": "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    "ag-font-size": "13px",
    "ag-row-height": "42px",
    "ag-header-height": "48px"
  }
};

const opCentralDash = {
  "id": "opcentral-dash",
  "name": "OpCentral Dashboard",
  "description": "Modern theme for OpCentral Dashboard with service cards and status indicators",
  "mode": "light",
  "variables": {
    // Background colors
    "color-background": "#f5f5f5",
    "color-background-elevated": "#ffffff",
    "color-background-overlay": "rgba(0, 0, 0, 0.5)",
    
    // Text colors
    "color-text-primary": "#1a1a1a",
    "color-text-secondary": "#6b7280",
    "color-text-tertiary": "#9ca3af",
    "color-text-inverse": "#ffffff",
    
    // Brand colors
    "color-primary": "#2563eb",
    "color-primary-hover": "#1d4ed8",
    "color-primary-light": "#dbeafe",
    
    // Status colors
    "color-success": "#10b981",
    "color-success-light": "#d1fae5",
    "color-error": "#ef4444",
    "color-error-light": "#fee2e2",
    "color-warning": "#f59e0b",
    "color-warning-light": "#fef3c7",
    "color-info": "#3b82f6",
    "color-info-light": "#dbeafe",
    
    // Border colors
    "color-border": "#e5e7eb",
    "color-border-hover": "#d1d5db",
    "color-border-focus": "#2563eb",
    
    // Component colors
    "color-card-background": "#ffffff",
    "color-card-border": "#e5e7eb",
    "color-card-shadow": "rgba(0, 0, 0, 0.1)",
    "color-card-hover": "#f9fafb",
    
    // Button colors
    "color-button-primary": "#2563eb",
    "color-button-primary-hover": "#1d4ed8",
    "color-button-secondary": "#6b7280",
    "color-button-secondary-hover": "#4b5563",
    
    // Header colors
    "color-header-background": "#1e3a8a",
    "color-header-text": "#ffffff",
    "color-header-border": "#1e40af",
    
    // Tab colors
    "color-tab-background": "rgba(255, 255, 255, 0.1)",
    "color-tab-background-active": "#2563eb",
    "color-tab-border": "rgba(255, 255, 255, 0.2)",
    "color-tab-text": "#ffffff",
    
    // Typography
    "font-family": "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif",
    "font-size-base": "14px",
    "font-size-small": "12px",
    "font-size-large": "16px",
    "font-size-xlarge": "20px",
    "font-weight-normal": "400",
    "font-weight-medium": "500",
    "font-weight-semibold": "600",
    "font-weight-bold": "700",
    
    // Spacing
    "spacing-xs": "0.25rem",
    "spacing-sm": "0.5rem",
    "spacing-md": "1rem",
    "spacing-lg": "1.5rem",
    "spacing-xl": "2rem",
    
    // Border radius
    "border-radius-sm": "4px",
    "border-radius-md": "6px",
    "border-radius-lg": "8px",
    "border-radius-xl": "12px",
    
    // Shadows
    "shadow-sm": "0 1px 2px rgba(0, 0, 0, 0.05)",
    "shadow-md": "0 4px 6px rgba(0, 0, 0, 0.1)",
    "shadow-lg": "0 10px 15px rgba(0, 0, 0, 0.1)",
    "shadow-xl": "0 20px 25px rgba(0, 0, 0, 0.15)",
    
    // Transitions
    "transition-fast": "150ms",
    "transition-base": "200ms",
    "transition-slow": "300ms"
  }
};

export const defaultSkins = [
  compactSkin,
  gridLight,
  gridDark,
  opCentralDash,
  ...fmwvtoolSkins
];

/**
 * Register default skins with a Skinnr instance
 */
export const registerDefaultSkins = (skinnr: any) => {
  defaultSkins.forEach(skin => {
    skinnr.registerSkin(skin.id, skin);
  });
};

export default defaultSkins;
